import React, { useEffect, useState } from 'react'
import {
  Box, Button, Checkbox, Chip, Drawer, IconButton, Link, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material'
import {
  ArrowBackRounded, ArrowForwardRounded, ChevronLeftRounded, ChevronRightRounded, CloseRounded, DescriptionOutlined, DesktopWindowsOutlined,
  GroupsRounded, Inventory2Outlined, ReceiptLongOutlined, ViewAgendaOutlined, WebOutlined, WidgetsOutlined
} from '@mui/icons-material'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import useAuthorization from '../generic/hooks/useAuthorization/useAuthorization'
import { useDateFormatter } from '../generic/hooks/useDateFormatter'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { deleteEvidenceImages, getVisionIncidents } from './visionApi'
import PhoneEvidence, { DeleteConfirmation, refusalText } from './PhoneEvidence'
import { useUser } from '../generic/hooks/useUser'
import { COVER_REASON_ICON } from './coverReasonIcon'
import { COVER_REASON_COLOR } from './visionState'
import {
  DEFAULT_RANGE, FROM_CAMERA_CONTROL, INCIDENT_REASON_TEXT, OPEN_NOW_QUERY, RANGES, REASONS_WITH_ALARM, SENSITIVE_TYPE_TEXT,
  VIEWED_TYPE_TEXT, equipmentText, howItEnded, humanDuration, incidentsQuery, whenText
} from './visionIncidentFormat'

// C2: Sun's page size by default.
const PAGE_SIZE = 50
// The open durations move on while the page stays open.
const CLOCK_MS = 30000

const NO_FILTERS = { range: DEFAULT_RANGE, from: '', to: '', reason: '', sensitiveType: '', q: '', onlyOpen: false }

// Design v2 (Registro.dc.html, PO 2026-10-22): every reason is a filled chip; CROWD, the one that never covers, in olive.
const OPEN_COLOUR = '#FF8F00'
const REASON_LOOK = {
  ...COVER_REASON_COLOR,
  CROWD: { background: '#BDC15C', foreground: '#1F1F1F' }
}
const REASON_ICON = { ...COVER_REASON_ICON, CROWD: GroupsRounded }

// What was being viewed, with an icon per type; MIC (scope ALL_MIC, C13.4) is the whole of MIC.
const VIEWED_ICON = {
  MIC: DesktopWindowsOutlined,
  SCREEN: WebOutlined,
  DOCUMENT: DescriptionOutlined,
  WIDGET: WidgetsOutlined,
  PART_REFERENCE: Inventory2Outlined,
  ORDER: ReceiptLongOutlined,
  FABRICATION_ORDER: ViewAgendaOutlined
}

// The path the "← Camera control" link goes back to.
const CAMERA_CONTROL_PATH = '/backoffice/cameraControl'

// C17b (RN164): only the profiles that configure Camera control delete phone images, by their exact code.
const PROFILES_THAT_DELETE_IMAGES = ['ADMIN', 'SUPERADMIN']
const DELETE_COLOUR = '#F68D76'

const typeText = (incident, t_) => (VIEWED_TYPE_TEXT[incident.sensitiveType] ? t_(VIEWED_TYPE_TEXT[incident.sensitiveType]) : (incident.sensitiveType || ''))

// The row says what was seen by its name; with no name (or MIC), by its type.
const viewedName = (incident, t_) => {
  if (incident.sensitiveType === 'MIC') {
    return t_('All of MIC')
  }
  return incident.sensitiveName || typeText(incident, t_)
}

// The detail says the type too, once when it is the same.
const viewedWithType = (incident, t_) => {
  const name = viewedName(incident, t_)
  const type = typeText(incident, t_)
  return incident.sensitiveType === 'MIC' || name === type ? name : `${type} · ${name}`
}

const ReasonChip = ({ reason, t_ }) => {
  const look = REASON_LOOK[reason] || REASON_LOOK.SERVICE_UNAVAILABLE
  const Icon = REASON_ICON[reason] || REASON_ICON.SERVICE_UNAVAILABLE
  return (
    <Chip
      size='small'
      data-testid='vision-incident-reason'
      data-reason={reason}
      icon={<Icon style={{ color: 'inherit' }} />}
      label={INCIDENT_REASON_TEXT[reason] ? t_(INCIDENT_REASON_TEXT[reason]) : reason}
      sx={{ fontWeight: 600, bgcolor: look.background, color: look.foreground }}
    />
  )
}

// An open incident: how long it has been open, counted from its start.
const OpenChip = ({ incident, now, t_ }) => (
  <Chip
    size='small'
    variant='outlined'
    data-testid='vision-incident-open'
    label={t_('Open · {{duration}}', { duration: humanDuration((now - Date.parse(incident.startedAt)) / 1000) })}
    sx={{ borderColor: OPEN_COLOUR, color: OPEN_COLOUR, fontWeight: 600 }}
  />
)

const DurationCell = ({ incident, now, t_ }) => (incident.open === true
  ? <OpenChip incident={incident} now={now} t_={t_} />
  : <Box component='span' sx={{ whiteSpace: 'nowrap' }}>{humanDuration(incident.durationSeconds)}</Box>)

// A labelled select of the filter bar, whose first option ('') is "All".
const FilterSelect = ({ id, label, options, value, onChange, t_ }) => (
  <TextField
    id={id}
    select
    size='small'
    label={t_(label)}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    // "All" is the '' option: shown as chosen, not as an empty field (S120).
    SelectProps={{ displayEmpty: true }}
    InputLabelProps={{ shrink: true }}
    sx={{ minWidth: 170 }}
  >
    <MenuItem value=''>{t_('All')}</MenuItem>
    {Object.entries(options).map(([code, text]) => <MenuItem key={code} value={code}>{t_(text)}</MenuItem>)}
  </TextField>
)

const IncidentDetail = ({ incident, now, dateFormat, canSeeAlarms, canDeleteImages, onEvidenceDeleted, onGone, onClose, t_ }) => {
  const when = (iso) => whenText(iso, { now, dateFormat, t_, withSeconds: true }) || '—'
  const fields = [
    ['User', incident.username],
    ['Equipment', equipmentText(incident, t_)],
    ['What was being viewed', viewedWithType(incident, t_)],
    ['Start', when(incident.startedAt)],
    ['End', incident.open === true ? '—' : when(incident.endedAt)],
    ['How it ended', howItEnded(incident, t_)],
    ['Maximum people', typeof incident.peakPeople === 'number' ? String(incident.peakPeople) : '—']
  ]
  return (
    <Stack spacing={2.25} sx={{ width: 380, maxWidth: '100vw', p: 3, boxSizing: 'border-box' }}>
      <Stack direction='row' alignItems='center' spacing={1.25}>
        <ReasonChip reason={incident.reason} t_={t_} />
        <DurationCell incident={incident} now={now} t_={t_} />
        <IconButton aria-label={t_('Close')} onClick={onClose} sx={{ ml: 'auto !important' }}>
          <CloseRounded />
        </IconButton>
      </Stack>
      <Box component='dl' sx={{ m: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
        {fields.map(([term, value]) => (
          <Box key={term} sx={term === 'What was being viewed' ? { gridColumn: '1 / -1' } : {}}>
            <Typography component='dt' variant='caption' sx={{ color: '#A8A8A8' }}>{t_(term)}</Typography>
            <Typography component='dd' variant='body2' sx={{ m: 0, mt: 0.25, color: 'text.primary' }}>{value}</Typography>
          </Box>
        ))}
      </Box>
      {/* C17.7 (RN158): only an incident that brings the `evidence` key (ADMIN or SUPERADMIN) has the phone image block. */}
      {Object.prototype.hasOwnProperty.call(incident, 'evidence') && (
        <PhoneEvidence
          key={incident.id}
          incident={incident}
          now={now}
          dateFormat={dateFormat}
          canDelete={canDeleteImages}
          onDeleted={(evidence) => onEvidenceDeleted(incident.id, evidence)}
          onGone={onGone}
          t_={t_}
        />
      )}
      {canSeeAlarms && REASONS_WITH_ALARM.includes(incident.reason) && (
        <Link component={RouterLink} to='/maintenance/alarms' underline='none' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 600 }}>
          {t_('View in Alarms')}
          <ArrowForwardRounded fontSize='small' />
        </Link>
      )}
    </Stack>
  )
}

/**
 * Story 213 — "Vision incidents" (DP-12, CU8, C11.9), design v2 approved by the PO (2026-10-22, Registro.dc.html): the
 * incident log under Maintenance, for SETTINGS. One bar of filters (quick ranges or two dates, reason, what was seen, a
 * search sent as `q` — H-21 — and "Only open" with the total of `?open=true&size=1`, DB-5); each row its reason chip, the
 * user, what was being viewed by its name, when ("Today 10:00", DP-14 local time) and the duration in plain words, or
 * "Open · …" for an open one, which stands out. Pressing a row opens its detail. Coming from Camera control, a link
 * goes back to it.
 */
const VisionIncidents = () => {
  const { t_ } = useSmartTranslation()
  const { format: dateFormat } = useDateFormatter()
  const location = useLocation()
  const isUserAuthorized = useAuthorization()
  const { user } = useUser()
  const canDeleteImages = PROFILES_THAT_DELETE_IMAGES.includes((user && user.profile && user.profile.code) || '')
  const fromCameraControl = location.state && location.state.from === FROM_CAMERA_CONTROL.from
  const [filters, setFilters] = useState(NO_FILTERS)
  const [page, setPage] = useState(0)
  const [incidents, setIncidents] = useState({ items: [], total: 0 })
  const [openNow, setOpenNow] = useState(0)
  const [selectedId, setSelectedId] = useState('')
  const [now, setNow] = useState(() => Date.now())
  // C17b (S408, S409): the images ticked on the page shown, the confirmation of deleting them, what the last deletion said
  // and a counter that asks the page again with the same filters.
  const [chosen, setChosen] = useState([])
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [notice, setNotice] = useState('')
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), CLOCK_MS)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    let cancelled = false
    setChosen([])
    getVisionIncidents(incidentsQuery(filters, page, Date.now()))
      .then((result) => {
        if (!cancelled) {
          setIncidents({ items: Array.isArray(result?.items) ? result.items : [], total: Number(result?.total) || 0 })
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [filters, page, reload])

  useEffect(() => {
    let cancelled = false
    getVisionIncidents(OPEN_NOW_QUERY)
      .then((result) => {
        if (!cancelled) setOpenNow(Number(result?.total) || 0)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const changeFilter = (name, value) => {
    setFilters((previous) => ({ ...previous, [name]: value }))
    setPage(0)
  }
  const changeField = (name) => (event) => changeFilter(name, event.target.value)

  const when = (iso) => whenText(iso, { now, dateFormat: dateFormat.date, t_ })
  const selected = incidents.items.find((incident) => incident.id === selectedId)

  // C17b (RN167, supuestos 6 y 7): a checkbox only on a phone row whose image is still there.
  const deletable = (incident) => canDeleteImages && incident.reason === 'PHONE' && Boolean(incident.evidence) && incident.evidence.status === 'AVAILABLE'
  const deletableShown = incidents.items.filter(deletable).map((incident) => incident.id)
  const toggleChosen = (id) => setChosen((previous) => (previous.includes(id) ? previous.filter((other) => other !== id) : [...previous, id]))
  const allChosen = deletableShown.length > 0 && deletableShown.every((id) => chosen.includes(id))
  const toggleAllChosen = () => setChosen(allChosen ? [] : deletableShown)
  const deleteChosen = () => {
    setConfirmingDelete(false)
    deleteEvidenceImages(chosen)
      .then((result) => {
        const deleted = Number(result && result.deleted) || 0
        const skipped = Number(result && result.skipped) || 0
        setNotice(t_('{{count}} images deleted', { count: deleted }) + (skipped > 0 ? ' · ' + t_('{{count}} were no longer available', { count: skipped }) : ''))
        setReload((previous) => previous + 1)
      })
      .catch((error) => setNotice(refusalText((error && error.status) || 500, t_)))
  }
  // S410 (H-C17b-4): the row takes the evidence Sun answered, so a deleted image loses its checkbox.
  const evidenceDeleted = (id, evidence) => {
    setIncidents((previous) => ({ ...previous, items: previous.items.map((incident) => (incident.id === id ? { ...incident, evidence } : incident)) }))
    setChosen((previous) => previous.filter((other) => other !== id))
  }
  const firstShown = page * PAGE_SIZE + 1
  const lastShown = Math.min((page + 1) * PAGE_SIZE, incidents.total)

  return (
    <Stack spacing={2.25}>
      <Stack spacing={0.75}>
        {fromCameraControl && (
          <Link component={RouterLink} to={CAMERA_CONTROL_PATH} underline='none' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 600, alignSelf: 'flex-start' }}>
            <ArrowBackRounded fontSize='small' />
            {t_('Camera control')}
          </Link>
        )}
        <Typography data-testid='vision-incidents-breadcrumb' variant='body2' sx={{ color: 'text.secondary' }}>
          <Link component={RouterLink} to='/maintenance' underline='hover' color='inherit'>{t_('Maintenance')}</Link> › {t_('Vision incidents')}
        </Typography>
        <Typography variant='h4' component='h1' sx={{ fontWeight: 600, color: 'text.primary' }}>{t_('Vision incidents')}</Typography>
      </Stack>

      <Paper role='group' aria-label={t_('Filters')} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2, p: 2, borderRadius: 3 }}>
        <Stack spacing={0.75}>
          <Typography id='vision-incidents-when' variant='caption' sx={{ fontWeight: 700, letterSpacing: 0.6, color: 'text.secondary', textTransform: 'uppercase' }}>{t_('When')}</Typography>
          <ToggleButtonGroup
            exclusive
            size='small'
            role='radiogroup'
            aria-labelledby='vision-incidents-when'
            value={filters.range}
            disabled={filters.onlyOpen}
            onChange={(event, value) => value && changeFilter('range', value)}
          >
            {RANGES.map((option) => (
              <ToggleButton key={option.value} value={option.value} role='radio' aria-checked={filters.range === option.value} sx={{ px: 1.75, textTransform: 'none' }}>
                {t_(option.text)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
        {filters.range === 'DATES' && !filters.onlyOpen && (
          <>
            <TextField id='vision-incidents-from' type='datetime-local' size='small' label={t_('Start date')} value={filters.from} onChange={changeField('from')} InputLabelProps={{ shrink: true }} />
            <TextField id='vision-incidents-to' type='datetime-local' size='small' label={t_('End date')} value={filters.to} onChange={changeField('to')} InputLabelProps={{ shrink: true }} />
          </>
        )}
        <FilterSelect id='vision-incidents-reason' label='Reason' options={INCIDENT_REASON_TEXT} value={filters.reason} onChange={(value) => changeFilter('reason', value)} t_={t_} />
        <FilterSelect id='vision-incidents-type' label='What was being viewed' options={SENSITIVE_TYPE_TEXT} value={filters.sensitiveType} onChange={(value) => changeFilter('sensitiveType', value)} t_={t_} />
        <TextField
          id='vision-incidents-search'
          type='search'
          size='small'
          label={t_('Search')}
          placeholder={t_('User or content')}
          value={filters.q}
          onChange={changeField('q')}
          sx={{ flex: '1 1 220px' }}
        />
        <Button
          variant='outlined'
          aria-pressed={filters.onlyOpen}
          onClick={() => changeFilter('onlyOpen', !filters.onlyOpen)}
          startIcon={<Box component='span' sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: OPEN_COLOUR }} />}
          sx={{
            borderRadius: 999,
            minHeight: 40,
            textTransform: 'none',
            color: 'text.primary',
            borderColor: filters.onlyOpen ? OPEN_COLOUR : '#555555',
            bgcolor: filters.onlyOpen ? '#4A3A1E' : 'transparent'
          }}
        >
          {t_('Only open')}&nbsp;<strong data-testid='vision-incidents-open-now'>{openNow}</strong>
        </Button>
      </Paper>

      {notice && <Typography role='status' variant='body2' sx={{ color: 'text.primary' }}>{notice}</Typography>}
      {chosen.length > 0 && (
        <Paper role='region' aria-label={t_('{{count}} images selected', { count: chosen.length })} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 600, mr: 'auto' }}>{t_('{{count}} images selected', { count: chosen.length })}</Typography>
          <Button onClick={() => setChosen([])} sx={{ borderRadius: 999, color: '#CFCFCF' }}>{t_('Cancel')}</Button>
          <Button variant='outlined' onClick={() => setConfirmingDelete(true)} sx={{ borderRadius: 999, color: DELETE_COLOUR, borderColor: DELETE_COLOUR, fontWeight: 700 }}>
            {t_('Delete images ({{count}})', { count: chosen.length })}
          </Button>
        </Paper>
      )}
      <DeleteConfirmation
        open={confirmingDelete}
        title={t_('Delete {{count}} images?', { count: chosen.length })}
        several={chosen.length > 1}
        onCancel={() => setConfirmingDelete(false)}
        onConfirm={deleteChosen}
        t_={t_}
      />

      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              {canDeleteImages && (
                <TableCell padding='checkbox'>
                  <Checkbox
                    checked={allChosen}
                    indeterminate={!allChosen && chosen.length > 0}
                    disabled={deletableShown.length === 0}
                    onChange={toggleAllChosen}
                    inputProps={{ 'aria-label': t_('Select the images on this page') }}
                  />
                </TableCell>
              )}
              {['Reason', 'User', 'What was being viewed', 'When', 'Duration'].map((column) => (
                <TableCell key={column}>{t_(column)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {incidents.items.map((incident) => {
              const open = incident.open === true
              const Icon = VIEWED_ICON[incident.sensitiveType] || DescriptionOutlined
              return (
                <TableRow
                  key={incident.id}
                  hover
                  tabIndex={0}
                  data-testid='vision-incident-row'
                  data-id={incident.id}
                  data-open={open}
                  onClick={() => setSelectedId(incident.id)}
                  onKeyDown={(event) => event.key === 'Enter' && setSelectedId(incident.id)}
                  selected={incident.id === selectedId}
                  sx={{ cursor: 'pointer', ...(open ? { boxShadow: `inset 4px 0 0 ${OPEN_COLOUR}`, bgcolor: 'rgba(255,143,0,.07)' } : {}) }}
                >
                  {canDeleteImages && (
                    // Ticking does not open the detail (Registro v4).
                    <TableCell padding='checkbox' onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                      {deletable(incident) && (
                        <Checkbox
                          checked={chosen.includes(incident.id)}
                          onChange={() => toggleChosen(incident.id)}
                          inputProps={{ 'aria-label': `${incident.username} · ${when(incident.startedAt)}` }}
                        />
                      )}
                    </TableCell>
                  )}
                  <TableCell><ReasonChip reason={incident.reason} t_={t_} /></TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>{incident.username}</TableCell>
                  <TableCell>
                    <Stack direction='row' alignItems='center' spacing={1}>
                      <Icon fontSize='small' sx={{ color: '#A8A8A8' }} />
                      <span>{viewedName(incident, t_)}</span>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(incident.startedAt)}</TableCell>
                  <TableCell><DurationCell incident={incident} now={now} t_={t_} /></TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {incidents.total > 0 && (
        <Stack direction='row' justifyContent='flex-end' alignItems='center' spacing={1}>
          <Typography data-testid='vision-incidents-pager' variant='body2' sx={{ color: 'text.secondary' }}>
            {t_('{{from}}–{{to}} of {{total}}', { from: firstShown, to: lastShown, total: incidents.total })}
          </Typography>
          <IconButton aria-label={t_('Previous page')} disabled={page === 0} onClick={() => setPage(page - 1)}>
            <ChevronLeftRounded />
          </IconButton>
          <IconButton aria-label={t_('Next page')} disabled={lastShown >= incidents.total} onClick={() => setPage(page + 1)}>
            <ChevronRightRounded />
          </IconButton>
        </Stack>
      )}

      <Drawer
        anchor='right'
        open={Boolean(selected)}
        onClose={() => setSelectedId('')}
        PaperProps={{ role: 'dialog', 'aria-label': t_('Incident detail') }}
      >
        {selected && (
          <IncidentDetail
            incident={selected}
            now={now}
            dateFormat={dateFormat.date}
            canSeeAlarms={isUserAuthorized('ALARMS')}
            canDeleteImages={canDeleteImages}
            onEvidenceDeleted={evidenceDeleted}
            onGone={() => setReload((previous) => previous + 1)}
            onClose={() => setSelectedId('')}
            t_={t_}
          />
        )}
      </Drawer>
    </Stack>
  )
}

export default VisionIncidents
